import React, { useMemo } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import TopBar from '../layout/TopBar';
import DivisionSummaryPanel from '../components/dashboard/DivisionSummaryPanel';
import { useHGS } from '../context/HGSContext';
import { ROLES } from '../data/mockData';
import { ORG_TREE } from '../data/orgTree';
import { computeResidualScore, bandFor, RISK_CATEGORIES } from '../data/riskUniverse';
import {
  ShieldAlert, Compass, CheckCircle2, ClipboardCheck, FileSignature,
  Send, ArrowRight, AlertTriangle, Target, Activity, Layers,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Operating Head Dashboard.
 *
 * Scope per governance ownership model: OH evaluates operational impact of
 * Admin-assigned risks. No enterprise governance analytics, no enterprise
 * heatmap, no enterprise escalation monitoring — those belong to Admin /
 * GRC Secretariat (/admin/grc-dashboard).
 */
export default function DashboardPage() {
  const { user, riskUniverse, applicability, evaluations, masters, formResponses, pendingApprovals } = useHGS();
  const navigate = useNavigate();
  const role = ROLES.find((r) => r.id === user?.role);

  const ohId = user?.operatingHead;
  const ohLabel = user?.ohLabel || ORG_TREE[ohId]?.label || '—';
  const ohColor = ORG_TREE[ohId]?.color || '#CC0000';

  // ── Assigned risks: master risks where applicability includes my OH ──
  const myRisks = useMemo(() => {
    if (!ohId) return [];
    return riskUniverse.filter((r) => (applicability?.[r.id] || []).includes(ohId));
  }, [riskUniverse, applicability, ohId]);

  // ── Evaluation status per assigned risk (my OH only) ─────────────────
  const evalAnalytics = useMemo(() => {
    let evaluated = 0;
    let confirmed = 0;
    let preventiveFilled = 0;
    let recoveryFilled = 0;
    let highBand = 0;
    let mediumBand = 0;
    let watchBand = 0;
    let lowBand = 0;
    let totalResidual = 0;
    myRisks.forEach((r) => {
      const ev = evaluations?.[r.id]?.[ohId];
      if (!ev) return;
      // Treat any non-empty narrative or confirmation as "evaluated"
      const touched = ev.concretePotentialRisk || ev.currentSituation || ev.preventiveMeasures || ev.recoveryMeasures || ev.confirmed;
      if (touched) evaluated++;
      if (ev.confirmed) confirmed++;
      if ((ev.preventiveMeasures || '').trim()) preventiveFilled++;
      if ((ev.recoveryMeasures || '').trim()) recoveryFilled++;
      const { residual } = computeResidualScore(ev);
      totalResidual += residual;
      const band = bandFor(residual);
      if (band === 'High') highBand++;
      else if (band === 'Medium') mediumBand++;
      else if (band === 'Watch') watchBand++;
      else lowBand++;
    });
    const total = myRisks.length;
    return {
      total,
      evaluated,
      confirmed,
      preventiveFilled,
      recoveryFilled,
      highBand, mediumBand, watchBand, lowBand,
      avgResidual: total === 0 ? 0 : (totalResidual / total).toFixed(1),
      evaluationPct: total === 0 ? 0 : Math.round((evaluated / total) * 100),
      confirmationPct: total === 0 ? 0 : Math.round((confirmed / total) * 100),
    };
  }, [myRisks, evaluations, ohId]);

  // ── Final approvals pending for OH ───────────────────────────────────
  const finalApprovalsPending = useMemo(() => {
    const responseLevel = Object.entries(formResponses || {})
      .filter(([, r]) => r?.workflowStatus === 'Approved')
      .map(([key, r]) => ({
        kind: 'response',
        key,
        moduleKey: r.moduleKey,
        recordId: r.recordId,
        department: r.submittedDepartment,
        submittedBy: r.submittedBy,
        at: r.submittedAt,
      }));
    const masterLevel = (pendingApprovals || [])
      .filter((m) => m.status === 'Pending')
      .map((m) => ({
        kind: 'master',
        key: m.id,
        moduleKey: m.moduleKey,
        recordId: m.proposedVersion,
        department: m.moduleLabel,
        submittedBy: m.submittedBy,
        at: m.submittedAt,
      }));
    return [...responseLevel, ...masterLevel];
  }, [formResponses, pendingApprovals]);

  // ── Operational Risk Summary — by classification, with linked compliance counts ─
  const linkedCompliance = useMemo(() => {
    // For each riskId, count linked rows across masters
    const map = {};
    ['fraud', 'pcc', 'dlc'].forEach((mk) => {
      (masters?.[mk] || []).forEach((row) => {
        if (row.linkedRiskId) {
          map[row.linkedRiskId] = map[row.linkedRiskId] || { fraud: 0, pcc: 0, dlc: 0 };
          map[row.linkedRiskId][mk]++;
        }
      });
    });
    return map;
  }, [masters]);

  const summaryByCategory = useMemo(() => {
    const byCat = {};
    myRisks.forEach((r) => {
      const ev = evaluations?.[r.id]?.[ohId];
      const { residual } = computeResidualScore(ev || r);
      const band = bandFor(residual);
      const linked = linkedCompliance[r.id] || { fraud: 0, pcc: 0, dlc: 0 };
      byCat[r.category] = byCat[r.category] || { total: 0, confirmed: 0, high: 0, medium: 0, fraud: 0, pcc: 0, dlc: 0 };
      byCat[r.category].total++;
      if (ev?.confirmed) byCat[r.category].confirmed++;
      if (band === 'High') byCat[r.category].high++;
      else if (band === 'Medium') byCat[r.category].medium++;
      byCat[r.category].fraud += linked.fraud;
      byCat[r.category].pcc += linked.pcc;
      byCat[r.category].dlc += linked.dlc;
    });
    return byCat;
  }, [myRisks, evaluations, ohId, linkedCompliance]);

  // Admin should never see the OH dashboard — bounce them to GRC Dashboard.
  // (Hooks above must run unconditionally; redirect is the return value only.)
  if (user?.role === 'admin') {
    return <Navigate to="/admin/grc-dashboard" replace />;
  }

  return (
    <div className="flex-1 flex flex-col bg-slate-50 min-h-screen">
      <TopBar
        title={`${role?.label || 'Operating Head'} Dashboard`}
        subtitle={`${ohLabel} · Operational Risk Evaluator · ${evalAnalytics.total} assigned risks`}
        breadcrumbs={['HGS', 'Operating Head', 'My Dashboard']}
        actions={
          <button
            onClick={() => navigate('/risk')}
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg shadow-sm transition"
            data-testid="oh-open-risk-evaluation"
          >
            <ClipboardCheck className="w-4 h-4" />
            Open Risk Evaluation
          </button>
        }
      />

      <div className="px-6 py-6 space-y-6">
        {/* ── Identity banner ───────────────────────────────────────── */}
        <div className="rounded-xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-5 border border-slate-700 shadow-sm" data-testid="oh-identity-banner">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: ohColor + '22' }}>
              <Compass className="w-6 h-6" style={{ color: ohColor }} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: ohColor }}>
                Operating Head · Operational Evaluator
              </div>
              <div className="text-lg font-bold mt-0.5">{ohLabel} — Operational Risk Workspace</div>
              <div className="text-sm text-slate-300 mt-1 max-w-3xl">
                You evaluate the operational impact of risks assigned by the GRC Secretariat. Frame each risk in your
                operational context, define preventive &amp; recovery controls, score frequency / impact / control,
                and sign off final compliance approvals routed from Division Heads.
              </div>
            </div>
            <div className="hidden md:flex items-center gap-5 text-right">
              <Tile label="Assigned" value={evalAnalytics.total} />
              <Tile label="Confirmed" value={`${evalAnalytics.confirmed}/${evalAnalytics.total}`} />
              <Tile label="Approvals" value={finalApprovalsPending.length} />
            </div>
          </div>
        </div>

        {/* ── OH KPIs ───────────────────────────────────────────────── */}
        <Section title="My Workspace" icon={Activity}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <OhKpi
              label="My Assigned Risks"
              value={evalAnalytics.total}
              icon={ShieldAlert}
              accent="#0F172A"
              onClick={() => navigate('/risk')}
              testid="oh-kpi-assigned"
            />
            <OhKpi
              label="Evaluated"
              value={`${evalAnalytics.evaluated}/${evalAnalytics.total}`}
              secondary={`${evalAnalytics.evaluationPct}%`}
              icon={ClipboardCheck}
              accent="#D97706"
              onClick={() => navigate('/risk')}
              testid="oh-kpi-evaluated"
            />
            <OhKpi
              label="Confirmed"
              value={`${evalAnalytics.confirmed}/${evalAnalytics.total}`}
              secondary={`${evalAnalytics.confirmationPct}%`}
              icon={CheckCircle2}
              accent="#059669"
              onClick={() => navigate('/risk')}
              testid="oh-kpi-confirmed"
            />
            <OhKpi
              label="Pending Final Approvals"
              value={finalApprovalsPending.length}
              icon={Send}
              accent="#7C3AED"
              onClick={() => navigate('/approvals')}
              testid="oh-kpi-approvals"
            />
          </div>
        </Section>

        {/* ── Risk Evaluation Progress ──────────────────────────────── */}
        <Section title="Risk Evaluation Progress" icon={Target}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <EvalProgressCard
              label="Evaluation Completeness"
              progressPct={evalAnalytics.evaluationPct}
              accent={ohColor}
              rows={[
                { label: 'Evaluated', value: `${evalAnalytics.evaluated}/${evalAnalytics.total}` },
                { label: 'Confirmed', value: `${evalAnalytics.confirmed}/${evalAnalytics.total}` },
                { label: 'Preventive controls filled', value: `${evalAnalytics.preventiveFilled}/${evalAnalytics.total}` },
                { label: 'Recovery controls filled', value: `${evalAnalytics.recoveryFilled}/${evalAnalytics.total}` },
              ]}
            />
            <ResidualBreakdownCard analytics={evalAnalytics} />
            <DegreeOfRiskCard analytics={evalAnalytics} accent={ohColor} />
          </div>
        </Section>

        {/* ── Final Approvals Pending ───────────────────────────────── */}
        <Section title="Pending Final Approvals" icon={Send}>
          <FinalApprovalsCard items={finalApprovalsPending} onOpen={(it) => navigate(it.kind === 'master' ? '/approvals?tab=master' : '/approvals')} />
        </Section>

        {/* ── Operational Risk Summary ──────────────────────────────── */}
        <Section title="Operational Risk Summary" icon={Layers}>
          <OperationalRiskTable
            summary={summaryByCategory}
            onCategoryClick={() => navigate('/risk')}
          />
          <div className="text-[10px] text-slate-500 italic mt-2">
            Counts reflect risks assigned to <b>{ohLabel}</b> only. "Linked compliance" totals show Fraud / PCC / DLC controls mapped to risks in each classification.
          </div>
        </Section>

        {/* ── Division-wise Summary (existing OH-scoped component) ───── */}
        <DivisionSummaryPanel />
      </div>
    </div>
  );
}

// ─── Layout helpers ─────────────────────────────────────────────────────
function Section({ title, icon: Icon, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-slate-600" />
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function OhKpi({ label, value, secondary, icon: Icon, accent, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      type="button"
      className="text-left bg-white rounded-xl border border-slate-200 p-4 hover:border-slate-300 hover:shadow-sm transition"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: accent + '15', color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <div className="text-xl font-bold tabular-nums text-slate-900 leading-none">{value}</div>
            {secondary && <div className="text-xs font-semibold text-slate-500 tabular-nums">{secondary}</div>}
          </div>
        </div>
      </div>
    </button>
  );
}

function EvalProgressCard({ label, progressPct, accent, rows }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-base font-bold tabular-nums" style={{ color: accent }}>{progressPct}%</div>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div className="h-full transition-all" style={{ width: `${progressPct}%`, backgroundColor: accent }} />
      </div>
      <ul className="space-y-1 text-xs">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between">
            <span className="text-slate-600">{r.label}</span>
            <span className="font-mono font-bold text-slate-800 tabular-nums">{r.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ResidualBreakdownCard({ analytics }) {
  const bands = [
    { key: 'highBand',   label: 'High',   color: '#CC0000', bg: '#FEE2E2', text: '#991B1B' },
    { key: 'mediumBand', label: 'Medium', color: '#D97706', bg: '#FED7AA', text: '#9A3412' },
    { key: 'watchBand',  label: 'Watch',  color: '#CA8A04', bg: '#FEF08A', text: '#854D0E' },
    { key: 'lowBand',    label: 'Low',    color: '#059669', bg: '#BBF7D0', text: '#166534' },
  ];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="residual-breakdown">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-3">Residual Risk Band Distribution</div>
      <div className="space-y-2">
        {bands.map((b) => {
          const v = analytics[b.key] || 0;
          const pct = analytics.total === 0 ? 0 : Math.round((v / analytics.total) * 100);
          return (
            <div key={b.key} className="flex items-center gap-2">
              <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider w-14 text-center" style={{ backgroundColor: b.bg, color: b.text }}>
                {b.label}
              </span>
              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full" style={{ width: `${pct}%`, backgroundColor: b.color }} />
              </div>
              <span className="text-xs font-mono tabular-nums text-slate-700 w-10 text-right">{v}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DegreeOfRiskCard({ analytics, accent }) {
  // Avg residual score in 1–25 → percent
  const pct = Math.min(100, Math.round((Number(analytics.avgResidual) / 25) * 100));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col" data-testid="degree-of-risk">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Avg Degree of Risk</div>
      <div className="flex items-baseline gap-2 mt-1">
        <div className="text-3xl font-bold tabular-nums text-slate-900" style={{ color: accent }}>{analytics.avgResidual}</div>
        <div className="text-xs text-slate-500">/ 25</div>
      </div>
      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-2">
        <div className="h-full transition-all" style={{ width: `${pct}%`, backgroundColor: accent }} />
      </div>
      <div className="text-[10px] text-slate-500 italic mt-3 leading-snug flex-1">
        Frequency × Impact ÷ Control across all assigned risks for your operation. Lower is healthier.
      </div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-[10px]">
        <div className="px-2 py-1 rounded bg-red-50 text-red-700 flex items-center justify-between">
          <span className="font-bold uppercase tracking-wider">High</span>
          <span className="font-mono tabular-nums">{analytics.highBand}</span>
        </div>
        <div className="px-2 py-1 rounded bg-amber-50 text-amber-700 flex items-center justify-between">
          <span className="font-bold uppercase tracking-wider">Medium</span>
          <span className="font-mono tabular-nums">{analytics.mediumBand}</span>
        </div>
      </div>
    </div>
  );
}

function FinalApprovalsCard({ items, onOpen }) {
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-6 flex items-center gap-3" data-testid="final-approvals-empty">
        <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-800">No items awaiting your final sign-off</div>
          <div className="text-[11px] text-slate-500 mt-0.5">Division Heads will route PCC / Fraud / DLC responses here once approved at division level.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="final-approvals-list">
      {items.slice(0, 8).map((it) => (
        <button
          key={it.key}
          onClick={() => onOpen(it)}
          className="w-full text-left px-4 py-3 hover:bg-slate-50/60 transition flex items-center gap-3"
          data-testid={`approval-row-${it.key}`}
        >
          <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
            it.kind === 'master' ? 'bg-violet-50 text-violet-700' : 'bg-sky-50 text-sky-700'
          )}>
            {it.kind === 'master' ? <FileSignature className="w-4 h-4" /> : <ClipboardCheck className="w-4 h-4" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cn('px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider',
                it.kind === 'master' ? 'bg-violet-100 text-violet-700' : 'bg-sky-100 text-sky-700'
              )}>
                {it.kind === 'master' ? 'MASTER · ' + (it.moduleKey || '').toUpperCase() : (it.moduleKey || '').toUpperCase()}
              </span>
              <span className="font-mono text-[10px] text-slate-500">{it.recordId}</span>
              {it.department && <span className="text-[10px] text-slate-500 truncate">· {it.department}</span>}
            </div>
            <div className="text-xs text-slate-700 mt-1 truncate">Submitted by <b>{it.submittedBy}</b> · routed for OH approval</div>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
        </button>
      ))}
      {items.length > 8 && (
        <div className="px-4 py-2 text-[11px] text-slate-500 italic">+ {items.length - 8} more — open Approvals for full list</div>
      )}
    </div>
  );
}

function OperationalRiskTable({ summary, onCategoryClick }) {
  const rows = RISK_CATEGORIES.filter((c) => summary[c]);
  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 px-5 py-6 flex items-center gap-3" data-testid="operational-summary-empty">
        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-slate-500" />
        </div>
        <div className="text-sm text-slate-600">No risks have been assigned to your operation yet by the GRC Secretariat.</div>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="operational-risk-summary">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Classification</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Risks</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Confirmed</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">High</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Medium</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Linked Fraud</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Linked PCC</th>
            <th className="text-right px-4 py-2 font-bold uppercase tracking-wider text-[10px] text-slate-600">Linked DLC</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((cat) => {
            const r = summary[cat];
            return (
              <tr key={cat} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition" onClick={onCategoryClick} data-testid={`opsumm-row-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                <td className="px-4 py-2 font-semibold text-slate-800">{cat}</td>
                <td className="text-right px-4 py-2 font-mono tabular-nums">{r.total}</td>
                <td className="text-right px-4 py-2 font-mono tabular-nums text-emerald-700">{r.confirmed}/{r.total}</td>
                <td className="text-right px-4 py-2 font-mono"><span className={r.high > 0 ? 'font-bold text-red-700' : 'text-slate-300'}>{r.high}</span></td>
                <td className="text-right px-4 py-2 font-mono"><span className={r.medium > 0 ? 'font-bold text-amber-700' : 'text-slate-300'}>{r.medium}</span></td>
                <td className="text-right px-4 py-2 font-mono"><span className={r.fraud > 0 ? 'text-sky-700' : 'text-slate-300'}>{r.fraud}</span></td>
                <td className="text-right px-4 py-2 font-mono"><span className={r.pcc > 0 ? 'text-slate-700 font-semibold' : 'text-slate-300'}>{r.pcc}</span></td>
                <td className="text-right px-4 py-2 font-mono"><span className={r.dlc > 0 ? 'text-violet-700' : 'text-slate-300'}>{r.dlc}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
